import React, { useEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';
import {Link} from 'react-router-dom';
import classNames from 'classnames';
import {Skeleton} from '@chakra-ui/react';
import {IUser} from 'types/user';
import {APP_PATHS, BREAKPOINTS} from '../../../../constants';
import Text from 'components/typografy/text';
import useWindowSize from 'hooks/useWindowSize';
import {Avatar} from 'components/chakra-ui/avatar';

const Info: React.FC<{userData: IUser | null; loading: boolean; userStats: any}> = ({userData: user, loading, userStats: stats}) => {
  const {width} = useWindowSize();
  // Track previous balance to detect increases across renders
  const prevBalanceRef = useRef<number | null | undefined>(null); // Ref to store the previous balance, init with null
  const [animate, setAnimate] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // On first mount, load the previously saved balance from localStorage
  // This ensures balance kept after mount/ page refresh/ new sessions
  // Or if it does not exist, try set it to the current balance from stats
  useEffect(() => {
    const saved = localStorage.getItem('prevBalance');
    if (saved != null) {
      prevBalanceRef.current = Number(saved);
    } else if (stats?.balance != null) {
      prevBalanceRef.current = Number(stats.balance);
    }
  }, []);
  

  // Check for balance changes, user change, on first mount or future remounts (unmount and then mount again. it does not equal to render, it only happens when the component is removed from the DOM and then added again)
  useEffect(() => {
    // If user is not available do not run the effect
    if (!user || stats?.balance == null){
      return;
    }
  
    const currentBalance = Number(stats.balance); // Get the current balance from stats
    // Check if: balance has increased, if it has play sound and show animation for 3 seconds
    // **Note:** This will not trigger on the first mount, as prevBalance yet to be set
    // **Note:** This will not trigger if stats.balance is null, since it useless check
    if (prevBalanceRef.current != null && currentBalance > prevBalanceRef.current) {
      setAnimate(true); // Trigger animation
      if (audioRef.current) {
        audioRef.current.play().catch(() => {}); // Play sound effect
      }
      setTimeout(() => setAnimate(false), 3000); // Reset animation flag
    }

    // Always update the ref even if the balance hasn't changed
    // This ensures that the next render will have the correct previous balance
    prevBalanceRef.current = currentBalance;
    // Save in local storage the current balance to keep value across future mounts
    localStorage.setItem('prevBalance', String(currentBalance));
  }, [stats, user]);
  
  // If user is not available, return null to avoid rendering the user info component in the header
  if (!user) return null;

  return (
    <div className={styles.wrapper}>
      {/* Sound effect */}
      <audio ref={audioRef} src="/sounds/cash_register_kaching.mp3" preload="auto" style={{ display: 'none' }}/>

      <Link to={APP_PATHS.CASHOUT} className={styles.boxWrapper}>
        {loading ? (
          <Skeleton height="40px" width="100px" className={styles.skeleton} />
        ) : (
          <div className={styles.coinWrapper}>
            <div className={classNames(styles.box, styles.iconCoinWrapper)}>
              <img src={'/images/icon.png'} alt="TonzOcash Icon" />
            </div>
            <div className={classNames(styles.box, styles.gradient, { [styles.animateBalance]: animate })}>
              ${Number(stats?.balance ?? 0).toFixed(1)}
            </div>
          </div>
        )}
      </Link>
      <div className={styles.box}>
        {loading ? (
          <Skeleton height="40px" width="130px" className={styles.skeleton} />
        ) : (
          <Link to={APP_PATHS.PROFILE} className={classNames({[styles.avatar]: width > BREAKPOINTS.MD})}>
            {width > BREAKPOINTS.MD ? (
              <>
                <div className={styles.name}>
                  <Avatar name={user.email} colorPalette="green" variant="outline" />
                </div>

                <Text variant={'large'} nativeClassname={styles.elipsis}>
                  {user.email}
                </Text>
              </>
            ) : (
              <Avatar name={user.email} colorPalette="green" variant="outline" />
            )}
          </Link>
        )}
      </div>
    </div>
  );
};

export default Info;
